import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiCode, FiZap, FiShield, FiUsers, FiTrendingUp, FiGitBranch, FiCheck } from 'react-icons/fi';

function BecomeDeveloper() {
  const navigate = useNavigate();
  const isLoggedIn = localStorage.getItem('token');
  const userRole = localStorage.getItem('userRole');
  const isDeveloper = userRole === 'developer';

  // If already a developer, show different content
  const handleGetStarted = () => {
    if (isDeveloper) {
      navigate('/dashboard');
    } else if (isLoggedIn) {
      // User is logged in but not a developer - they need to upgrade
      navigate('/register/dev');
    } else {
      navigate('/register/dev');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 py-20 px-6">
        <div className="container mx-auto max-w-5xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {isDeveloper ? (
              <>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 rounded-full mb-6">
                  <FiCheck /> You're already a developer!
                </div>
                <h1 className="text-5xl font-bold mb-6 text-white">
                  Welcome, Developer!
                </h1>
                <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
                  You already have full access to all developer tools. Start building amazing extensions for Cronos Browser.
                </p>
                <div className="flex gap-4 justify-center flex-wrap">
                  <Link
                    to="/dashboard"
                    className="px-8 py-4 bg-white text-gray-900 rounded-xl font-bold hover:bg-gray-100 transition-all shadow-lg"
                  >
                    Go to Dashboard
                  </Link>
                  <Link
                    to="/build"
                    className="px-8 py-4 bg-green-600 text-white rounded-xl font-bold hover:bg-green-500 transition-all"
                  >
                    Build Extension
                  </Link>
                  <Link
                    to="/docs"
                    className="px-8 py-4 bg-gray-700 text-white rounded-xl font-bold hover:bg-gray-600 transition-all"
                  >
                    View Documentation
                  </Link>
                </div>
              </>
            ) : (
              <>
                <h1 className="text-5xl font-bold mb-6 text-white">
                  Become an AVALW Developer
                </h1>
                <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
                  Join the growing community of developers building powerful extensions for Cronos Browser. Share your creativity and reach users worldwide.
                </p>
                <div className="flex gap-4 justify-center flex-wrap">
                  <button
                    onClick={handleGetStarted}
                    className="px-8 py-4 bg-white text-gray-900 rounded-xl font-bold hover:bg-gray-100 transition-all shadow-lg"
                  >
                    {isLoggedIn ? 'Upgrade to Developer' : 'Get Started Free'}
                  </button>
                  <Link
                    to="/docs"
                    className="px-8 py-4 bg-gray-700 text-white rounded-xl font-bold hover:bg-gray-600 transition-all"
                  >
                    View Documentation
                  </Link>
                </div>
              </>
            )}
          </motion.div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-16 px-6">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Why Build on AVALW?
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="p-8 bg-white border-2 border-gray-200 rounded-2xl hover:border-gray-900 transition-all"
            >
              <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center mb-4">
                <FiShield className="text-white text-2xl" />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-gray-900">Code Protection</h3>
              <p className="text-gray-600 leading-relaxed">
                Your source code is compiled into secure binary format. Protect your intellectual property from theft and reverse engineering.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="p-8 bg-white border-2 border-gray-200 rounded-2xl hover:border-gray-900 transition-all"
            >
              <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center mb-4">
                <FiGitBranch className="text-white text-2xl" />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-gray-900">Open Ecosystem</h3>
              <p className="text-gray-600 leading-relaxed">
                Build and share extensions freely. Join a collaborative community focused on innovation and quality user experiences.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="p-8 bg-white border-2 border-gray-200 rounded-2xl hover:border-gray-900 transition-all"
            >
              <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center mb-4">
                <FiUsers className="text-white text-2xl" />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-gray-900">Large Audience</h3>
              <p className="text-gray-600 leading-relaxed">
                Reach millions of Cronos Browser users worldwide. Get your extensions featured in our curated collections.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="p-8 bg-white border-2 border-gray-200 rounded-2xl hover:border-gray-900 transition-all"
            >
              <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center mb-4">
                <FiCode className="text-white text-2xl" />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-gray-900">Easy Development</h3>
              <p className="text-gray-600 leading-relaxed">
                Simple JavaScript-based API. Use standard web technologies you already know. Full documentation and examples provided.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="p-8 bg-white border-2 border-gray-200 rounded-2xl hover:border-gray-900 transition-all"
            >
              <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center mb-4">
                <FiZap className="text-white text-2xl" />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-gray-900">Fast Review</h3>
              <p className="text-gray-600 leading-relaxed">
                Extensions are reviewed within 24-48 hours. Get your updates live quickly with our automated build system.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="p-8 bg-white border-2 border-gray-200 rounded-2xl hover:border-gray-900 transition-all"
            >
              <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center mb-4">
                <FiTrendingUp className="text-white text-2xl" />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-gray-900">Analytics & Insights</h3>
              <p className="text-gray-600 leading-relaxed">
                Track downloads, active users, and ratings. Make data-driven decisions to improve your extensions and grow your user base.
              </p>
            </motion.div>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="py-16 px-6 bg-gray-50">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            How to Get Started
          </h2>

          <div className="space-y-8">
            <div className="flex gap-6 items-start">
              <div className="w-12 h-12 bg-gray-900 text-white rounded-full flex items-center justify-center flex-shrink-0 text-xl font-bold">
                1
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-2 text-gray-900">Create Developer Account</h3>
                <p className="text-gray-600 leading-relaxed">
                  Sign up for a free developer account. Verify your email and complete your profile with basic information.
                </p>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="w-12 h-12 bg-gray-900 text-white rounded-full flex items-center justify-center flex-shrink-0 text-xl font-bold">
                2
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-2 text-gray-900">Build Your Extension</h3>
                <p className="text-gray-600 leading-relaxed">
                  Use our comprehensive documentation to build your extension. Or use our AI Assistant tool to generate code without programming knowledge.
                </p>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="w-12 h-12 bg-gray-900 text-white rounded-full flex items-center justify-center flex-shrink-0 text-xl font-bold">
                3
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-2 text-gray-900">Upload & Compile</h3>
                <p className="text-gray-600 leading-relaxed">
                  Upload your source files through the developer dashboard. Our system automatically compiles your code into secure .avalw binary format.
                </p>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="w-12 h-12 bg-gray-900 text-white rounded-full flex items-center justify-center flex-shrink-0 text-xl font-bold">
                4
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-2 text-gray-900">Test & Submit</h3>
                <p className="text-gray-600 leading-relaxed">
                  Download the compiled extension and test it in Cronos Browser. When ready, submit for review with screenshots and description.
                </p>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="w-12 h-12 bg-gray-900 text-white rounded-full flex items-center justify-center flex-shrink-0 text-xl font-bold">
                5
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-2 text-gray-900">Publish & Share</h3>
                <p className="text-gray-600 leading-relaxed">
                  After approval, your extension goes live on the marketplace. Users can discover, install, and rate it. Track analytics and gather feedback to improve.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Resources */}
      <div className="py-16 px-6">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Developer Resources
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            <Link
              to="/docs"
              className="p-8 bg-white border-2 border-gray-200 rounded-2xl hover:border-gray-900 transition-all"
            >
              <h3 className="text-2xl font-bold mb-3 text-gray-900">Documentation</h3>
              <p className="text-gray-600 leading-relaxed">
                Complete technical documentation, API reference, and best practices for building AVALW extensions.
              </p>
            </Link>

            <Link
              to="/ai-assistant"
              className="p-8 bg-white border-2 border-gray-200 rounded-2xl hover:border-gray-900 transition-all"
            >
              <h3 className="text-2xl font-bold mb-3 text-gray-900">AI Assistant</h3>
              <p className="text-gray-600 leading-relaxed">
                No coding experience? Use our AI-powered tool to generate extension code by describing your idea in plain English.
              </p>
            </Link>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-16 px-6 bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="container mx-auto max-w-4xl text-center">
          {isDeveloper ? (
            <>
              <h2 className="text-4xl font-bold mb-6 text-white">
                Ready to Build Something Amazing?
              </h2>
              <p className="text-xl text-gray-300 mb-8">
                You have all the tools you need. Start creating your next extension now!
              </p>
              <Link
                to="/build"
                className="inline-block px-8 py-4 bg-white text-gray-900 rounded-xl font-bold hover:bg-gray-100 transition-all shadow-lg"
              >
                Start Building
              </Link>
            </>
          ) : (
            <>
              <h2 className="text-4xl font-bold mb-6 text-white">
                Ready to Start Building?
              </h2>
              <p className="text-xl text-gray-300 mb-8">
                Join our developer community and start creating amazing extensions today.
              </p>
              <button
                onClick={handleGetStarted}
                className="inline-block px-8 py-4 bg-white text-gray-900 rounded-xl font-bold hover:bg-gray-100 transition-all shadow-lg"
              >
                {isLoggedIn ? 'Upgrade to Developer' : 'Create Developer Account'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default BecomeDeveloper;
